import React, { useState } from "react";
import { Check } from "lucide-react";
import useInView from "../../hooks/useInView";

const RATE_TABS = ["Regular", "Specials", "Junior", "Cart & Packages", "League", "Corporate"];

const RatesSection = () => {
  const [ratesRef, ratesInView] = useInView();
  const [activeRateTab, setActiveRateTab] = useState("Regular");

  return (
    <section ref={ratesRef} id="tee-times" className="py-24 bg-white">
      <div id="rates" className="absolute" style={{ marginTop: "-80px", visibility: "hidden" }} />
      <div className="max-w-5xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <div className="text-center mb-10">
          <span className="text-[#C9A96E] text-sm font-medium tracking-[0.2em] uppercase">Green Fees</span>
          <h2 className="text-4xl font-serif font-bold text-[#1B2A4A] mt-3 mb-5">Rates & Fees</h2>
          <div className="inline-flex items-center gap-2 bg-[#1B2A4A] text-white text-sm font-medium px-5 py-2 rounded-full">
            <Check size={13} className="text-[#C9A96E]" /> All prices are tax included
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="overflow-x-auto mb-8 pb-1">
          <div className="flex gap-2 min-w-max mx-auto justify-center">
            {RATE_TABS.map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveRateTab(tab)}
                className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                  activeRateTab === tab
                    ? "bg-[#1B2A4A] text-white shadow-md"
                    : "bg-[#f8f7f4] text-gray-600 hover:bg-[#1B2A4A]/10"
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className={`transition-all duration-500 ${ratesInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>

          {/* Regular Rates */}
          {activeRateTab === "Regular" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl mx-auto">
              <div className="bg-[#f8f7f4] rounded-2xl p-6">
                <div className="inline-block bg-[#C9A96E]/20 text-[#1B2A4A] text-xs font-bold px-3 py-1 rounded-full mb-3 uppercase tracking-wide">Regular Rates</div>
                <div className="text-xs text-gray-500 mb-4">Tuesday – Sunday</div>
                <div className="space-y-3">
                  {[
                    { label: "18 Holes — Open to 11AM", walk: "$50", cart: "$75" },
                    { label: "18 Holes — 11AM to 5PM", walk: "$45", cart: "$70" },
                    { label: "9 Holes — Any Time", walk: "$40", cart: "$50" },
                  ].map((row) => (
                    <div key={row.label} className="text-sm border-b border-gray-200 pb-2 last:border-0 last:pb-0">
                      <div className="text-[#1B2A4A] font-medium mb-1">{row.label}</div>
                      <div className="flex justify-between text-gray-600">
                        <span>Golf Only</span>
                        <span className="font-bold text-[#1B2A4A]">{row.walk}</span>
                      </div>
                      <div className="flex justify-between text-gray-600">
                        <span>Golf &amp; Cart</span>
                        <span className="font-bold text-[#1B2A4A]">{row.cart}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-[#f8f7f4] rounded-2xl p-6">
                <div className="inline-block bg-[#C9A96E]/20 text-[#1B2A4A] text-xs font-bold px-3 py-1 rounded-full mb-3 uppercase tracking-wide">Monday Special</div>
                <div className="text-xs text-gray-500 mb-4">Mondays including Holidays · Any Age</div>
                <div className="space-y-3">
                  {[
                    { label: "9 Holes Walking", price: "$35" },
                    { label: "9 Holes with Cart", price: "$45" },
                    { label: "18 Holes Walking", price: "$40" },
                    { label: "18 Holes with Cart", price: "$60" },
                  ].map((item) => (
                    <div key={item.label} className="flex justify-between text-sm border-b border-gray-200 pb-2 last:border-0 last:pb-0">
                      <span className="text-gray-600">{item.label}</span>
                      <span className="font-bold text-[#1B2A4A]">{item.price}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Specials */}
          {activeRateTab === "Specials" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-[#1B2A4A] rounded-2xl p-6 text-white flex flex-col">
                <div className="inline-block self-start bg-[#C9A96E] text-[#1B2A4A] text-xs font-bold px-3 py-1 rounded-full mb-4 uppercase tracking-wide">Twilight Special</div>
                <div className="text-4xl font-bold mb-1">$50</div>
                <div className="text-white/70 text-sm mb-4">Golf & Cart — Per Person</div>
                <div className="space-y-1.5 text-sm text-white/80 mt-auto">
                  <div>Available 7 days a week</div>
                  <div>5PM to Close</div>
                  <div className="text-[#C9A96E] text-xs pt-2">* Excluding League Times</div>
                </div>
              </div>
              <div className="bg-[#f8f7f4] rounded-2xl p-6">
                <div className="inline-block bg-[#C9A96E]/20 text-[#1B2A4A] text-xs font-bold px-3 py-1 rounded-full mb-3 uppercase tracking-wide">Senior Special</div>
                <div className="text-xs text-gray-500 mb-4">Tuesday – Friday · Age 50+</div>
                <div className="space-y-3">
                  {[
                    { label: "9 Holes Walking", price: "$35" },
                    { label: "9 Holes with Cart", price: "$45" },
                    { label: "18 Holes Walking", price: "$40" },
                    { label: "18 Holes with Cart", price: "$60" },
                  ].map((item) => (
                    <div key={item.label} className="flex justify-between text-sm border-b border-gray-200 pb-2 last:border-0 last:pb-0">
                      <span className="text-gray-600">{item.label}</span>
                      <span className="font-bold text-[#1B2A4A]">{item.price}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Junior */}
          {activeRateTab === "Junior" && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-[#f8f7f4] rounded-2xl p-6">
                <div className="inline-block bg-[#C9A96E]/20 text-[#1B2A4A] text-xs font-bold px-3 py-1 rounded-full mb-3 uppercase tracking-wide">Age 13 & Under</div>
                <div className="space-y-3 mt-4">
                  {[
                    { label: "9 Holes", price: "$15" },
                    { label: "18 Holes", price: "$25" },
                  ].map((item) => (
                    <div key={item.label} className="flex justify-between text-sm border-b border-gray-200 pb-2 last:border-0 last:pb-0">
                      <span className="text-gray-600">{item.label}</span>
                      <span className="font-bold text-[#1B2A4A]">{item.price}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-[#f8f7f4] rounded-2xl p-6">
                <div className="inline-block bg-[#C9A96E]/20 text-[#1B2A4A] text-xs font-bold px-3 py-1 rounded-full mb-3 uppercase tracking-wide">Age 14–18</div>
                <div className="space-y-3 mt-4">
                  {[
                    { label: "9 Holes", price: "$25" },
                    { label: "18 Holes", price: "$35" },
                  ].map((item) => (
                    <div key={item.label} className="flex justify-between text-sm border-b border-gray-200 pb-2 last:border-0 last:pb-0">
                      <span className="text-gray-600">{item.label}</span>
                      <span className="font-bold text-[#1B2A4A]">{item.price}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-[#1B2A4A] rounded-2xl p-6 text-white flex flex-col">
                <div className="inline-block self-start bg-[#C9A96E] text-[#1B2A4A] text-xs font-bold px-3 py-1 rounded-full mb-4 uppercase tracking-wide">Cart Add-On</div>
                <div className="text-4xl font-bold mb-1">$10</div>
                <div className="text-white/70 text-sm">Per Round (9 or 18 Holes)</div>
                <div className="text-white/50 text-xs mt-auto pt-4">Available any day for all junior golfers</div>
              </div>
            </div>
          )}

          {/* Cart & Packages */}
          {activeRateTab === "Cart & Packages" && (
            <div className="space-y-10">
              <div>
                <h3 className="text-base font-semibold text-[#1B2A4A] mb-4 flex items-center gap-2">
                  <span className="w-1 h-5 bg-[#C9A96E] rounded-full inline-block"></span>
                  Cart Rental
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    { label: "18 Holes — Per Person", price: "$25" },
                    { label: "9 Holes — Per Person", price: "$15" },
                  ].map((item) => (
                    <div key={item.label} className="bg-[#f8f7f4] rounded-xl p-5 flex justify-between items-center">
                      <span className="text-gray-600 text-sm">{item.label}</span>
                      <span className="text-2xl font-bold text-[#1B2A4A]">{item.price}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h3 className="text-base font-semibold text-[#1B2A4A] mb-1 flex items-center gap-2">
                  <span className="w-1 h-5 bg-[#C9A96E] rounded-full inline-block"></span>
                  Cart Packages
                </h3>
                <p className="text-xs text-gray-400 mb-4 ml-3">Good 7 days a week for the 2026 season only</p>
                <div className="overflow-hidden rounded-2xl shadow-sm border border-gray-100">
                  <div className="grid grid-cols-3 bg-[#1B2A4A] text-white text-sm font-semibold py-3 px-6">
                    <div>Package</div>
                    <div className="text-center">9 Holes</div>
                    <div className="text-center">18 Holes</div>
                  </div>
                  {[
                    { pkg: "Pay 10, Get 12 Rides", nine: "$150", eighteen: "$250" },
                    { pkg: "Pay 20, Get 25 Rides", nine: "$300", eighteen: "$500" },
                  ].map((row, i) => (
                    <div key={row.pkg} className={`grid grid-cols-3 py-4 px-6 text-sm hover:bg-[#C9A96E]/5 transition-colors ${i % 2 === 0 ? "bg-white" : "bg-[#f8f7f4]"}`}>
                      <div className="text-[#1B2A4A] font-medium">{row.pkg}</div>
                      <div className="text-center text-gray-600">{row.nine}</div>
                      <div className="text-center text-gray-600">{row.eighteen}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h3 className="text-base font-semibold text-[#1B2A4A] mb-1 flex items-center gap-2">
                  <span className="w-1 h-5 bg-[#C9A96E] rounded-full inline-block"></span>
                  Round Packages
                </h3>
                <p className="text-xs text-gray-400 mb-4 ml-3">Good 7 days a week for the 2026 season only</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-[#f8f7f4] rounded-xl p-5">
                    <div className="text-sm font-semibold text-[#1B2A4A] mb-3 pb-2 border-b border-gray-200">9 Holes</div>
                    {[
                      { label: "Pay 10, Get 12 — Walking", price: "$400" },
                      { label: "Pay 10, Get 12 — with Cart", price: "$500" },
                    ].map((item) => (
                      <div key={item.label} className="flex justify-between text-sm py-2.5 border-b border-gray-200 last:border-0">
                        <span className="text-gray-600">{item.label}</span>
                        <span className="font-bold text-[#1B2A4A]">{item.price}</span>
                      </div>
                    ))}
                  </div>
                  <div className="bg-[#f8f7f4] rounded-xl p-5">
                    <div className="text-sm font-semibold text-[#1B2A4A] mb-3 pb-2 border-b border-gray-200">18 Holes</div>
                    {[
                      { label: "Pay 10, Get 12 — Walking", price: "$500" },
                      { label: "Pay 10, Get 12 — with Cart", price: "$750" },
                      { label: "Pay 20, Get 25 — Walking", price: "$1,000" },
                      { label: "Pay 20, Get 25 — with Cart", price: "$1,500" },
                    ].map((item) => (
                      <div key={item.label} className="flex justify-between text-sm py-2.5 border-b border-gray-200 last:border-0">
                        <span className="text-gray-600">{item.label}</span>
                        <span className="font-bold text-[#1B2A4A]">{item.price}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* League */}
          {activeRateTab === "League" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-base font-semibold text-[#1B2A4A] mb-4 flex items-center gap-2">
                  <span className="w-1 h-5 bg-[#C9A96E] rounded-full inline-block"></span>
                  League Rates
                </h3>
                <div className="overflow-hidden rounded-2xl shadow-sm border border-gray-100">
                  <div className="grid grid-cols-3 bg-[#1B2A4A] text-white text-sm font-semibold py-3 px-5">
                    <div></div>
                    <div className="text-center">Golf Only</div>
                    <div className="text-center">Golf & Cart</div>
                  </div>
                  {[
                    { label: "18 Holes", walk: "$40", cart: "$60" },
                    { label: "9 Holes", walk: "$35", cart: "$50" },
                  ].map((row, i) => (
                    <div key={row.label} className={`grid grid-cols-3 py-4 px-5 text-sm hover:bg-[#C9A96E]/5 transition-colors ${i % 2 === 0 ? "bg-white" : "bg-[#f8f7f4]"}`}>
                      <div className="text-[#1B2A4A] font-medium">{row.label}</div>
                      <div className="text-center text-gray-600">{row.walk}</div>
                      <div className="text-center text-gray-600">{row.cart}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h3 className="text-base font-semibold text-[#1B2A4A] mb-1 flex items-center gap-2">
                  <span className="w-1 h-5 bg-[#C9A96E] rounded-full inline-block"></span>
                  League Packages
                </h3>
                <p className="text-xs text-gray-400 mb-4 ml-3">Pre-pay for 20 league rounds</p>
                <div className="overflow-hidden rounded-2xl shadow-sm border border-gray-100">
                  <div className="grid grid-cols-3 bg-[#1B2A4A] text-white text-sm font-semibold py-3 px-5">
                    <div></div>
                    <div className="text-center">Golf Only</div>
                    <div className="text-center">Golf & Cart</div>
                  </div>
                  {[
                    { label: "18 Holes", walk: "$700", cart: "$1,100" },
                    { label: "9 Holes", walk: "$600", cart: "$800" },
                  ].map((row, i) => (
                    <div key={row.label} className={`grid grid-cols-3 py-4 px-5 text-sm hover:bg-[#C9A96E]/5 transition-colors ${i % 2 === 0 ? "bg-white" : "bg-[#f8f7f4]"}`}>
                      <div className="text-[#1B2A4A] font-medium">{row.label}</div>
                      <div className="text-center text-gray-600">{row.walk}</div>
                      <div className="text-center text-gray-600">{row.cart}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Corporate */}
          {activeRateTab === "Corporate" && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-[#f8f7f4] rounded-2xl p-6">
                  <div className="inline-block bg-[#C9A96E]/20 text-[#1B2A4A] text-xs font-bold px-3 py-1 rounded-full mb-3 uppercase tracking-wide">Corporate Membership #1</div>
                  <div className="space-y-3 text-sm text-gray-600">
                    <p>One primary member. The company may submit a list of <strong className="text-[#1B2A4A]">4 additional employees</strong>, each with the ability to bring one foursome per day at no charge, excluding league times.</p>
                    <p>Includes cart privileges, golf, and driving range access.</p>
                    <div className="border-t border-gray-200 pt-3 space-y-1.5">
                      <div className="flex justify-between">
                        <span>Additional play (same day) — Walking</span>
                        <span className="font-bold text-[#1B2A4A]">$40</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Additional play (same day) — With Cart</span>
                        <span className="font-bold text-[#1B2A4A]">$60</span>
                      </div>
                    </div>
                  </div>
                  <div className="mt-5 pt-4 border-t border-gray-200 flex items-center justify-between">
                    <span className="text-xs text-gray-500">Annual Membership</span>
                    <span className="text-2xl font-bold text-[#1B2A4A]">$6,000</span>
                  </div>
                  <p className="text-xs text-gray-400 text-right mt-0.5">taxes included</p>
                </div>
                <div className="bg-[#f8f7f4] rounded-2xl p-6">
                  <div className="inline-block bg-[#C9A96E]/20 text-[#1B2A4A] text-xs font-bold px-3 py-1 rounded-full mb-3 uppercase tracking-wide">Corporate Membership #2</div>
                  <div className="space-y-3 text-sm text-gray-600">
                    <p>One primary member. The company may submit a list of <strong className="text-[#1B2A4A]">up to 19 additional employees</strong> who may play at no charge, limited to one tee time per day, excluding league times.</p>
                    <p>Includes cart privileges, golf, and driving range access. Only the primary member may participate in leagues and club events.</p>
                    <div className="border-t border-gray-200 pt-3 space-y-1.5">
                      <div className="flex justify-between">
                        <span>Non-listed guests in tee time — Walking</span>
                        <span className="font-bold text-[#1B2A4A]">$40</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Non-listed guests in tee time — Riding</span>
                        <span className="font-bold text-[#1B2A4A]">$60</span>
                      </div>
                    </div>
                  </div>
                  <div className="mt-5 pt-4 border-t border-gray-200 flex items-center justify-between">
                    <span className="text-xs text-gray-500">Annual Membership</span>
                    <span className="text-2xl font-bold text-[#1B2A4A]">$6,000</span>
                  </div>
                  <p className="text-xs text-gray-400 text-right mt-0.5">taxes included</p>
                </div>
              </div>
              <p className="text-center text-xs text-gray-400 italic">*** Proof of corporate employment status will be required upon request from the corporate member's organizer ***</p>
            </div>
          )}

        </div>
      </div>
    </section>
  );
};

export { RatesSection };
